/**
 * WalletTransactions — W1.5 + F-E.2
 *
 * Paginierte Transaktions-Liste mit Load-More.
 * Isoliert in eigener Error-Boundary — Fehler hier crashen nicht die Detail-Page.
 * P1-Fix (Abgleich Prototyp wallet-detail.html): Zeilen zeigen Datum +
 * Klartext-Beschreibung + farbiger Betrag statt Roh-Betrag+Icon+relativer Zeit.
 * Eine Filter-Bar (Typ/Zeitraum, F-E.2) ist im Prototyp fuer diese Sektion
 * nicht vorgesehen und wurde entfernt.
 * Echte Gegenpartei-Klartexte ("Transfer to Cold Storage", "Commission
 * payment to <Name>") sind nicht abbildbar: GET /api/v2/wallets/:id/transactions
 * liefert nur signature/type/direction/token_symbol/amount/timestamp, kein
 * Memo/Gegenpartei-Feld (siehe wallets_controller#serialize_transaction) —
 * ohne Backend-Aenderung (verboten) bleibt eine generische, aber wahrheitsgemaesse
 * Richtungs-Beschreibung die ehrlichste Naeherung.
 */
import { Component, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useWalletTransactions } from './useWalletTransactions'
import { explorerUrl } from './walletUtils'
import { formatLocalDate } from '../../lib/locale'
import type { WalletTransaction } from './useWalletTransactions'

// --- Error-Boundary (Class-Component) ---

type EBProps = { children: ReactNode; fallback: ReactNode }
type EBState = { hasError: boolean }

class TransactionsErrorBoundary extends Component<EBProps, EBState> {
  constructor(props: EBProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): EBState {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}

// --- Fallback ---

function TransactionsFallback() {
  const { t } = useTranslation('wallets')
  return (
    <div className="error-state" role="alert">
      <p>{t('error.transactionsUnavailable')}</p>
    </div>
  )
}

// --- Skeleton ---

function TransactionSkeletonRow() {
  return (
    <div className="list-row skeleton" aria-hidden="true">
      <div className="skeleton-line" style={{ width: 60, height: 14 }} />
      <div className="skeleton-line" style={{ width: 140, height: 14, marginLeft: 12 }} />
      <div className="skeleton-line" style={{ width: 60, height: 14, marginLeft: 'auto' }} />
    </div>
  )
}

// --- Beschreibung (Datum + Klartext), P1-Fix Abgleich Prototyp ---

function useTxDescription(tx: WalletTransaction): string {
  const { t } = useTranslation('wallets')
  // `type` ist serverseitig praktisch immer 'transfer' (Default-Wert, siehe
  // Payment#set_default_type) — nur falls das Backend kuenftig spezifischere
  // Typen liefert, wird der Rohwert mitgenommen statt ihn zu verschlucken.
  if (tx.type && tx.type !== 'transfer') {
    return t('transactions.desc.typed', { type: tx.type, symbol: tx.token_symbol })
  }
  if (tx.direction === 'in') return t('transactions.desc.in', { symbol: tx.token_symbol })
  if (tx.direction === 'out') return t('transactions.desc.out', { symbol: tx.token_symbol })
  return t('transactions.desc.unknown', { symbol: tx.token_symbol })
}

// --- Transaction Row ---

function TransactionRow({ tx }: { tx: WalletTransaction }) {
  const { t } = useTranslation('wallets')
  const description = useTxDescription(tx)
  const isIn = tx.direction === 'in'
  const isOut = tx.direction === 'out'
  const sign = isIn ? '+' : isOut ? '−' : ''

  return (
    <div className="list-row tx-row" role="listitem">
      <span className="muted" style={{ fontSize: 12, minWidth: 84 }}>{formatLocalDate(tx.timestamp)}</span>
      <span className="ellipsis" style={{ flex: 1 }}>{description}</span>
      <span className="num-sm" style={{ color: isIn ? 'var(--success)' : 'var(--text)' }}>
        {sign}
        {tx.amount.toFixed(3)} {tx.token_symbol}
      </span>
      <a
        href={explorerUrl(tx.signature)}
        target="_blank"
        rel="noopener noreferrer"
        className="tx-explorer-link muted"
        aria-label={t('transactions.explorerLink', { sig: `${tx.signature.slice(0, 8)}...` })}
        style={{ marginLeft: 8, fontSize: '0.75rem' }}
        onClick={(e) => e.stopPropagation()}
      >
        ↗
      </a>
    </div>
  )
}

// --- Inner-Komponente (wird von Error-Boundary umhuellt) ---

function WalletTransactionsInner({ walletId }: { walletId: string }) {
  const { t } = useTranslation('wallets')

  const { transactions, isLoading, isError, hasMore, loadMore } = useWalletTransactions(walletId)

  if (isLoading && transactions.length === 0) {
    return (
      <div aria-busy="true" aria-label={t('loading.transactions')}>
        {Array.from({ length: 3 }).map((_, i) => (
          <TransactionSkeletonRow key={i} />
        ))}
      </div>
    )
  }

  if (isError && transactions.length === 0) {
    // Wird via Error-Boundary oder hier direkt angezeigt
    throw new Error('Transactions endpoint error')
  }

  if (transactions.length === 0) {
    return (
      <div className="empty-state">
        <p>{t('empty.noTransactions')}</p>
      </div>
    )
  }

  return (
    <div>
      <div className="list-rows" role="list" aria-label={t('transactions.listLabel')}>
        {transactions.map((tx) => (
          <TransactionRow key={tx.signature} tx={tx} />
        ))}
      </div>

      {hasMore && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <button
            className="btn btn-secondary"
            onClick={loadMore}
            disabled={isLoading}
            aria-label={t('transactions.loadMoreLabel')}
          >
            {isLoading ? t('transactions.loading') : t('transactions.loadMore')}
          </button>
        </div>
      )}
    </div>
  )
}

// --- Oeffentliche Export-Komponente ---

type Props = {
  walletId: string
}

export function WalletTransactions({ walletId }: Props) {
  return (
    <TransactionsErrorBoundary fallback={<TransactionsFallback />}>
      <WalletTransactionsInner walletId={walletId} />
    </TransactionsErrorBoundary>
  )
}
